import Box from '@mui/material/Box'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import Toolbar from '@mui/material/Toolbar'
import { useAuth } from '../../hooks/useAuth'
import { useCore } from '../../hooks/useCore'
import { ToastContainer } from 'react-toastify'
import { cookies } from '../../hooks/useCookies'
import { DarkMode } from '../../styles/darkMode'
// import useWebSocket from 'react-use-websocket'
import NavBar from '../../components/Core/NavBar'
import { LightMode } from '../../styles/lightMode'
import CssBaseline from '@mui/material/CssBaseline'
import SideBar, { drawerWidth } from '../../components/Core/SideBar'
import useWindowSize from '../../hooks/useWindowSize'
import { WebMenu } from './menu'
import { createContext, memo, useMemo, useState } from 'react'
import { createTheme, Grid, ThemeProvider, PaletteMode, Paper } from '@mui/material'

interface Props {
  window?: () => Window
}

const getDesignMode = (mode: PaletteMode) => ({
  palette: mode === 'light' ? LightMode : DarkMode,
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#65B32C',
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#65B32C',
        }
      }
    }
  }
})

const ColorModeContext = createContext({ toggleColorMode: () => { } })

const refresh = () => {
  window.location.reload()
}

function ResponsiveDrawer(props: Props) {
  const { user } = useAuth()
  const location = useLocation()
  const { displayAlt, sidebarCollapsed } = useCore()
  const { width } = useWindowSize()

  const isBlockedForWebProfile =
    user?.profile === '4' &&
    !WebMenu.includes(location.pathname) &&
    !location.pathname.startsWith('/query/')

  const [mode, setMode] = useState<PaletteMode>(cookies.get('@CheckView:themeMode') ?? 'light')

  const theme = useMemo(() => createTheme(getDesignMode(mode)), [mode])
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
        cookies.set('@CheckView:themeMode', mode === 'light' ? 'dark' : 'light')
        refresh()
      },
    }),
    [mode],
  )

  // const { lastJsonMessage } = useWebSocket(
  //   'wss://ntfy.sh/teste/ws', {
  //     onOpen: () => toast.success('Connected', {
  //       position: toast.POSITION.TOP_RIGHT
  //     }),
  //     onMessage: () => {
  //       if (lastJsonMessage) {
  //         const message = lastJsonMessage as {[key: string]: any}
  //         toast.info(message['message'], {
  //           position: toast.POSITION.TOP_RIGHT
  //         })
  //       }
  //     },
  //     onError: (event) => {
  //       toast.error(event.toString(), {
  //         position: toast.POSITION.TOP_RIGHT
  //       })
  //     },
  //     shouldReconnect: (closeEvent) => true,
  //     reconnectInterval: 5000
  //   }
  // )

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <ToastContainer />
        <Box sx={{ display: width >= 600 ? 'flex' : displayAlt }}>
          <CssBaseline />
          <NavBar />
          <SideBar />
          {/* Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: sidebarCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
            }}
          >
            <Toolbar />
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Paper
                sx={
                  {
                    width: '100%', 
                    marginBottom: '20px', 
                    padding: '20px'
                  }
                }
                variant="outlined"
              >
                {isBlockedForWebProfile ? <Navigate to="/" replace /> : <Outlet />}
              </Paper>
            </Grid>
          </Box>
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

ResponsiveDrawer.defaultProps = {
  displayAlt: 'flex'
}

export default memo(ResponsiveDrawer)